/**
 * New console — account panel.
 *
 * Everything about the signed-in person, from the avatar in the topbar or the name
 * at the foot of the rail. The prototype puts this behind both, and it is worth
 * keeping both: the rail is where you are looking when the console is wide, the
 * topbar is where you are looking when it is not.
 *
 * What the role can do is not repeated here — the Profile screen's "What you can do"
 * lists it; the panel just names the role and gets out of the way.
 */
import { useState } from 'react';

import useAuth from '@hooks/useAuth';
import useNotistack from '@hooks/useNotistack';

import { roleCan } from '../capabilities';
import useSignOut from '../useSignOut';
import { Panel } from './Overlay';
import { Badge, Button, initialsOf } from './Primitives';
import {
  IconUsers, IconKey, IconMail, IconCog, IconCommand, IconLogout, IconLayers, IconAccess, IconPolicy,
  IconLock, IconTrash
} from './Icons';

const AccountPanel = ({ onClose, onGo, onShortcuts, onA11y, onLock }) => {
  const { user, setUiTheme } = useAuth();
  const { notify } = useNotistack();
  const [switching, setSwitching] = useState(false);
  const doSignOut = useSignOut();

  const name = `${(user && user.first_name) || ''} ${(user && user.last_name) || ''}`.trim();
  const initials = initialsOf(name, 'U');
  const role = (user && user.role) || '';

  const go = (route) => () => { onClose(); onGo(route); };

  const signOut = () => { onClose(); doSignOut(); };

  return (
    <Panel title="Account" onClose={onClose}>
      <div className="nt-row" style={{ gap: 12, marginBottom: 16, flexWrap: 'nowrap' }}>
        <span className="nt-avatar lg">{initials}</span>
        <div className="nt-min0">
          <div style={{ fontWeight: 650 }}>{name || 'Signed in'}</div>
          <div style={{ fontSize: '.73rem', color: 'var(--nt-muted)' }}>{user && user.email}</div>
          <div style={{ marginTop: 5 }}><Badge tone="info">{role || 'No role'}</Badge></div>
        </div>
      </div>

      <div className="nt-stack nt-gap-6 nt-mt-16">
        <Button className="nt-menu-btn" onClick={go('/profile')}><IconUsers />Your profile</Button>
        <Button className="nt-menu-btn" onClick={go('/change-password')}><IconKey />Change password</Button>
        <Button className="nt-menu-btn" onClick={go('/change-email')}><IconMail />Change email</Button>
        <Button className="nt-menu-btn" onClick={go('/settings')}><IconCog />Settings</Button>
        {/* The archive lives here, as in the classic: a review screen, not daily work. */}
        {roleCan(role, 'leads.delete') ? (
          <Button className="nt-menu-btn" onClick={go('/deleted-leads')}><IconTrash />Deleted leads</Button>
        ) : null}
        <Button className="nt-menu-btn" onClick={() => { onClose(); onA11y(); }}>
          <IconAccess />Display and accessibility
        </Button>
        <Button className="nt-menu-btn" onClick={() => { onClose(); onShortcuts(); }}>
          <IconCommand />Keyboard shortcuts
        </Button>
        <Button className="nt-menu-btn" onClick={() => { onClose(); onLock(); }}>
          <IconLock />Lock the screen
        </Button>
        <Button className="nt-menu-btn" onClick={go('/privacy-policy')}><IconPolicy />Privacy policy</Button>
        {/* On success the page reloads into the classic console; only a failure comes back. */}
        <Button
          className="nt-menu-btn"
          disabled={switching}
          onClick={async () => {
            setSwitching(true);
            const r = await setUiTheme('classic');
            if (!r || !r.isOk) {
              setSwitching(false);
              notify((r && r.error) || 'Could not switch consoles. You are still on the new one.', 'error');
            }
          }}
        >
          <IconLayers />{switching ? 'Switching…' : 'Back to the classic console'}
        </Button>
        <Button className="nt-menu-btn" variant="danger" onClick={signOut}>
          <IconLogout />Sign out
        </Button>
      </div>
    </Panel>
  );
};

export default AccountPanel;
